import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';

const CAT_EMOJI = { crop: '🌾', livestock: '🐄', seed: '🌱', fertilizer: '🧪', pesticide: '💧', fuel: '⛽', material: '🔧', feed: '🌿', veterinary: '💉', other: '📦' };

// Group products by subcategory (fallback to product_type), preserving order.
function groupBySubcategory(list) {
  const map = new Map();
  for (const it of list) {
    const key = it.subcategory || it.product_type || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return [...map.entries()];
}

export default function MasterSelect({ type, filter, value, onChange, onSelect, placeholder, label, required, showAddToList, priorityCategory, usageType, farmTypeFilter }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [catalogResults, setCatalogResults] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const wrapperRef = useRef(null);

  const endpoint = type === 'contact' ? '/master/contacts' : '/farm-products';

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filter) {
      Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    }
    if (usageType && type === 'product') params.set('usageType', usageType);
    if (farmTypeFilter && type === 'product') params.set('farmType', farmTypeFilter);
    const qs = params.toString() ? `?${params.toString()}` : '';
    api.get(`${endpoint}${qs}`)
      .then(r => setItems(r.data.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [endpoint, search, filter, usageType, farmTypeFilter]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { setSearch(value || ''); }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false); setShowCatalog(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item) => {
    setSearch(item.name);
    onChange(item.name);
    if (onSelect) onSelect(item);
    setOpen(false); setShowCatalog(false);
  };

  const toggleFavourite = async (e, item) => {
    e.stopPropagation();
    const mpId = item.master_product_id || item.id;
    const newVal = !item.is_favourite;
    try {
      await api.put(`/farm-products/${mpId}/favourite`, { isFavourite: newVal });
      setItems(prev => prev.map(i => {
        const id = i.master_product_id || i.id;
        return id === mpId ? { ...i, is_favourite: newVal } : i;
      }));
    } catch { /* silent */ }
  };

  const handleAddNew = async () => {
    if (!search.trim()) return;
    try {
      let newItem;
      if (type === 'contact') {
        const res = await api.post('/master/contacts', { type: filter?.type || 'both', name: search.trim() });
        newItem = res.data;
      } else {
        const res = await api.post('/master/products', { name: search.trim(), category: filter?.category || 'other' });
        newItem = res.data;
        await api.post('/farm-products', { masterProductId: newItem.id });
        toast.success(`${newItem.name} listenize eklendi`);
      }
      setSearch(newItem.name);
      onChange(newItem.name);
      if (onSelect) onSelect(newItem);
      setOpen(false); setShowCatalog(false);
    } catch { /* silent */ }
  };

  const searchCatalog = async () => {
    if (!search.trim()) return;
    try {
      const r = await api.get(`/farm-products/catalog?search=${encodeURIComponent(search.trim())}`);
      setCatalogResults((r.data.data || []).filter(p => !p.is_selected));
      setShowCatalog(true);
    } catch { setCatalogResults([]); }
  };

  const addFromCatalog = async (item) => {
    try {
      await api.post('/farm-products', { masterProductId: item.id });
      toast.success(`${item.name} listenize eklendi`);
      handleSelect(item);
      load();
    } catch { toast.error('Eklenemedi'); }
  };

  // Filter and sort with priority
  const filtered = items.filter(i =>
    i.name.toLowerCase().includes((search || '').toLowerCase())
  );

  // Sort: favourites first, then priorityCategory within favourites, then by usage
  const sorted = [...filtered].sort((a, b) => {
    // Favourites always first
    if (a.is_favourite && !b.is_favourite) return -1;
    if (!a.is_favourite && b.is_favourite) return 1;
    // Within same favourite status, priorityCategory first
    if (priorityCategory && a.is_favourite && b.is_favourite) {
      if (a.category === priorityCategory && b.category !== priorityCategory) return -1;
      if (a.category !== priorityCategory && b.category === priorityCategory) return 1;
    }
    // Then by usage_count desc
    if ((a.usage_count || 0) !== (b.usage_count || 0)) return (b.usage_count || 0) - (a.usage_count || 0);
    return (a.name || '').localeCompare(b.name || '', 'tr');
  });

  const favourites = sorted.filter(i => i.is_favourite);
  const others = sorted.filter(i => !i.is_favourite);
  const exactMatch = sorted.some(i => i.name.toLowerCase() === (search || '').toLowerCase());

  const renderRow = (item) => {
    const emoji = type === 'product' ? (CAT_EMOJI[item.category] || '📦') : '';
    return (
      <div key={item.id || item.master_product_id || item.farm_product_id}
        onClick={() => handleSelect(item)}
        style={{ padding: '7px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: 6 }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {emoji && <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{emoji}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
          {type === 'product' && item.variety && (
            <span style={{ fontSize: '0.7rem', color: '#aaa' }}>{item.variety}</span>
          )}
          {type === 'contact' && (
            <div style={{ fontSize: '0.72rem', color: '#999' }}>
              {[item.city, item.phone].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <span style={{ fontSize: '0.72rem', color: '#aaa', flexShrink: 0 }}>{item.unit}</span>
        {type === 'product' && (
          <span onClick={(e) => toggleFavourite(e, item)}
            style={{ fontSize: '0.9rem', cursor: 'pointer', flexShrink: 0, padding: '0 2px' }}>
            {item.is_favourite ? '⭐' : '☆'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="form-group" ref={wrapperRef} style={{ position: 'relative' }}>
      {label && <label>{label}{required && ' *'}</label>}
      <input
        value={search}
        onChange={e => { setSearch(e.target.value); onChange(e.target.value); if (!open) setOpen(true); setShowCatalog(false); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || t('common.search')}
        required={required}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
          maxHeight: 280, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {loading && <div style={{ padding: 10, color: '#999', fontSize: '0.82rem' }}>{t('common.loading')}</div>}
          {!loading && sorted.length === 0 && !search && (
            <div style={{ padding: 10, color: '#999', fontSize: '0.82rem' }}>{t('common.noData')}</div>
          )}

          {/* Favourite section */}
          {favourites.length > 0 && (
            <>
              <div style={{ padding: '5px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#e67e22', background: '#fffcf5', borderBottom: '1px solid #f5eedd' }}>
                ⭐ {t('favourites.myProducts')}
              </div>
              {favourites.map(renderRow)}
            </>
          )}

          {/* Others section — grouped by subcategory for products */}
          {others.length > 0 && (
            <>
              {favourites.length > 0 && (
                <div style={{ padding: '5px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#888', background: '#f9f9f9', borderBottom: '1px solid #eee', borderTop: '1px solid #eee' }}>
                  {t('favourites.otherProducts')}
                </div>
              )}
              {type === 'product'
                ? groupBySubcategory(others).map(([sub, rows]) => (
                    <React.Fragment key={sub || '_'}>
                      {sub && (
                        <div style={{ padding: '4px 12px', fontSize: '0.68rem', fontWeight: 700, color: '#7a9472', background: '#fafdf8' }}>
                          {sub}
                        </div>
                      )}
                      {rows.map(renderRow)}
                    </React.Fragment>
                  ))
                : others.map(renderRow)}
            </>
          )}

          {/* Catalog search + add new */}
          {search.trim() && !exactMatch && !showCatalog && type === 'product' && (
            <>
              <div onClick={searchCatalog}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', color: '#3498db', fontWeight: 700, borderTop: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#eef6ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                🔍 "{search.trim()}" {t('farmProducts.searchCatalog')}
              </div>
              <div onClick={handleAddNew}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--green-mid)', fontWeight: 700 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                ➕ "{search.trim()}" {t('master.addNew')}
              </div>
            </>
          )}
          {search.trim() && !exactMatch && type === 'contact' && (
            <div onClick={handleAddNew}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--green-mid)', fontWeight: 700,
                borderTop: sorted.length > 0 ? '1px solid var(--border)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              ➕ "{search.trim()}" {t('master.addNew')}
            </div>
          )}

          {/* Catalog results */}
          {showCatalog && catalogResults.length > 0 && (
            <div style={{ borderTop: '2px solid var(--green-mid)', background: '#f8fff8' }}>
              <div style={{ padding: '6px 12px', fontSize: '0.72rem', color: '#888', fontWeight: 700 }}>
                {t('farmProducts.catalogResults')}
              </div>
              {catalogResults.map(item => (
                <div key={item.id} onClick={() => addFromCatalog(item)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #e0f0e0' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--green-pale)'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f8fff8'}>
                  <div style={{ fontWeight: 600 }}>{item.name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#999' }}>
                    {[item.product_type || item.category, item.unit, item.variety].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ))}
            </div>
          )}
          {showCatalog && catalogResults.length === 0 && (
            <div style={{ padding: 10, color: '#999', fontSize: '0.82rem', borderTop: '2px solid var(--green-mid)' }}>
              {t('farmProducts.noCatalogResults')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
